import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare, Send, ArrowLeft, Hash, AlertTriangle,
  RefreshCw, LogOut, CheckCheck, Bell, BellOff,
  CalendarClock, Lightbulb, Paperclip, X, Search,
  FileText, Image, Download, Heart, Plus, Pencil, Trash2, Users, Star,
  Utensils, Globe, ChefHat
} from 'lucide-react';
import { MobileNav } from '../components/MobileNav';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogoHome } from '../components/LogoHome';
import { useConfirm } from '../components/ConfirmProvider';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";
const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamChat() {
  const confirm = useConfirm();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState('general');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () => localStorage.getItem('visitex_chat_notif') !== 'off'
  );
  // File sharing
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  // Search
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Channel management
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState(null); // null = create, object = edit
  const [chanName, setChanName] = useState('');
  const [chanDesc, setChanDesc] = useState('');
  const [chanIcon, setChanIcon] = useState('hash');
  const [savingChannel, setSavingChannel] = useState(false);
  // @mention autocomplete
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const chatInputRef = useRef(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  // =================== DATA LOADING ===================
  const loadChannels = useCallback(async () => {
    try {
      const res = await chatApi.getChannels();
      setChannels(res.data);
    } catch (err) { /* silent */ }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const res = await chatApi.getMessages(activeChannel);
      const newMsgs = res.data;
      if (notificationsEnabled && prevMessageCountRef.current > 0 && newMsgs.length > prevMessageCountRef.current) {
        const latest = newMsgs[newMsgs.length - 1];
        if (latest && latest.sender_id !== user?.id && document.hidden) {
          showBrowserNotification(latest.sender_name, latest.text || 'Sent a file');
        }
      }
      prevMessageCountRef.current = newMsgs.length;
      setMessages(newMsgs);
    } catch (err) { /* silent */ }
    setLoading(false);
  }, [activeChannel, notificationsEnabled, user?.id]);

  const loadTyping = useCallback(async () => {
    try {
      const res = await chatApi.getTyping(activeChannel);
      setTypingUsers(res.data);
    } catch (err) { /* silent */ }
  }, [activeChannel]);

  // =================== EFFECTS ===================
  useEffect(() => { loadChannels(); }, [loadChannels]);

  useEffect(() => {
    setLoading(true);
    prevMessageCountRef.current = 0;
    setSearchOpen(false);
    setSearchResults([]);
    setSearchQuery('');
    loadMessages();
    chatApi.markRead(activeChannel).catch(() => {});
    pollRef.current = setInterval(() => {
      loadMessages();
      loadTyping();
      loadChannels();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeChannel, loadMessages, loadTyping, loadChannels]);

  useEffect(() => {
    if (!searchOpen) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, searchOpen]);

  useEffect(() => {
    if (messages.length > 0) chatApi.markRead(activeChannel).catch(() => {});
  }, [messages.length, activeChannel]);

  // =================== NOTIFICATIONS ===================
  const showBrowserNotification = (sender, text) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`Visitex - ${sender}`, { body: text.slice(0, 100), icon: LOGO_URL, tag: 'visitex-chat' });
    }
  };

  const toggleNotifications = async () => {
    if (!notificationsEnabled) {
      if ('Notification' in window && Notification.permission !== 'granted') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') { toast.error('Please allow notifications in browser settings'); return; }
      }
      localStorage.setItem('visitex_chat_notif', 'on');
      setNotificationsEnabled(true);
      toast.success('Chat notifications enabled');
    } else {
      localStorage.setItem('visitex_chat_notif', 'off');
      setNotificationsEnabled(false);
      toast.info('Chat notifications disabled');
    }
  };

  // =================== TYPING & @MENTIONS ===================
  const handleInputChange = (e) => {
    const val = e.target.value;
    setNewMessage(val);
    chatApi.setTyping(activeChannel, true).catch(() => {});
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      chatApi.setTyping(activeChannel, false).catch(() => {});
    }, 3000);

    // Detect active @mention token
    const caret = e.target.selectionStart ?? val.length;
    const before = val.slice(0, caret);
    const atIdx = before.lastIndexOf('@');
    if (atIdx !== -1) {
      const prevChar = atIdx > 0 ? before[atIdx - 1] : ' ';
      const token = before.slice(atIdx + 1);
      if ((prevChar === ' ' || prevChar === '\n' || atIdx === 0) && /^[A-Za-z0-9_-]{0,30}$/.test(token)) {
        setMentionStart(atIdx);
        setMentionQuery(token);
        setMentionOpen(true);
        chatApi.getMentionableUsers(token).then(res => {
          setMentionResults(res.data || []);
          setMentionIndex(0);
        }).catch(() => setMentionResults([]));
        return;
      }
    }
    setMentionOpen(false);
    setMentionStart(-1);
  };

  const insertMention = (u) => {
    if (mentionStart < 0) return;
    const before = newMessage.slice(0, mentionStart);
    const afterCaret = chatInputRef.current?.selectionStart ?? newMessage.length;
    const after = newMessage.slice(afterCaret);
    const newVal = `${before}@${u.handle} ${after}`;
    setNewMessage(newVal);
    setMentionOpen(false);
    setMentionStart(-1);
    setMentionResults([]);
    setTimeout(() => {
      if (chatInputRef.current) {
        const pos = before.length + u.handle.length + 2;
        chatInputRef.current.focus();
        chatInputRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleInputKeyDown = (e) => {
    if (!mentionOpen || mentionResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex(i => (i + 1) % mentionResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex(i => (i - 1 + mentionResults.length) % mentionResults.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(mentionResults[mentionIndex]);
    } else if (e.key === 'Escape') {
      setMentionOpen(false);
    }
  };

  // =================== SEND ===================
  const handleSend = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingFile) || sending) return;
    setSending(true);
    chatApi.setTyping(activeChannel, false).catch(() => {});
    clearTimeout(typingTimeoutRef.current);
    try {
      if (pendingFile) {
        await chatApi.uploadFile(activeChannel, newMessage.trim(), pendingFile);
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        await chatApi.sendMessage(activeChannel, newMessage.trim());
      }
      setNewMessage('');
      await loadMessages();
      loadChannels();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // =================== FILE ===================
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setPendingFile(file);
  };

  const cancelFile = () => {
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // =================== SEARCH ===================
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await chatApi.search(searchQuery.trim(), activeChannel);
      setSearchResults(res.data);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };
  const backRoute = user?.role === 'organisation' ? '/admin' : user?.role === 'supervisor' ? '/supervisor' : '/dashboard';

  const chatNavItems = [
    { to: backRoute, label: 'Back to Dashboard', icon: <ArrowLeft className="h-5 w-5" /> },
    { to: '/team-chat', label: 'Team Chat', icon: <MessageSquare className="h-5 w-5" />, active: true },
  ];

  const channelIcons = {
    general: <Hash className="h-4 w-4" />,
    'available-shifts': <CalendarClock className="h-4 w-4" />,
    'behaviour-ideas': <Lightbulb className="h-4 w-4" />,
    'peer-support': <Heart className="h-4 w-4" />,
    'meals-nutrition': <Utensils className="h-4 w-4" />,
    'cultural-food': <Globe className="h-4 w-4" />,
    'recipes-planning': <ChefHat className="h-4 w-4" />,
    handover: <RefreshCw className="h-4 w-4" />,
    urgent: <AlertTriangle className="h-4 w-4" />,
  };

  // Icon library for custom channels
  const iconMap = {
    hash: <Hash className="h-4 w-4" />,
    heart: <Heart className="h-4 w-4" />,
    lightbulb: <Lightbulb className="h-4 w-4" />,
    calendar: <CalendarClock className="h-4 w-4" />,
    alert: <AlertTriangle className="h-4 w-4" />,
    refresh: <RefreshCw className="h-4 w-4" />,
    star: <Star className="h-4 w-4" />,
    users: <Users className="h-4 w-4" />,
    utensils: <Utensils className="h-4 w-4" />,
    globe: <Globe className="h-4 w-4" />,
    'chef-hat': <ChefHat className="h-4 w-4" />,
  };

  const iconFor = (ch) => {
    if (ch.is_default !== false && channelIcons[ch.id]) return channelIcons[ch.id];
    if (ch.icon && iconMap[ch.icon]) return iconMap[ch.icon];
    return <Hash className="h-4 w-4" />;
  };

  const canManageChannel = (ch) => {
    if (ch.is_default !== false) return false;
    return ch.created_by === user?.id || user?.role === 'supervisor' || user?.role === 'organisation';
  };

  const openCreateChannel = () => {
    setEditingChannel(null);
    setChanName(''); setChanDesc(''); setChanIcon('hash');
    setChannelDialogOpen(true);
  };

  const openEditChannel = (ch, e) => {
    e?.stopPropagation?.();
    setEditingChannel(ch);
    setChanName(ch.name || '');
    setChanDesc(ch.description || '');
    setChanIcon(ch.icon || 'hash');
    setChannelDialogOpen(true);
  };

  const handleSaveChannel = async () => {
    if (!chanName.trim() || chanName.trim().length < 2) { toast.error('Channel name must be at least 2 characters'); return; }
    setSavingChannel(true);
    try {
      if (editingChannel) {
        await chatApi.updateChannel(editingChannel.id, {
          name: chanName.trim(), description: chanDesc, icon: chanIcon
        });
        toast.success('Channel updated');
      } else {
        const res = await chatApi.createChannel({
          name: chanName.trim(), description: chanDesc, icon: chanIcon
        });
        toast.success(`Channel "${res.data.name}" created`);
        setActiveChannel(res.data.id);
      }
      setChannelDialogOpen(false);
      loadChannels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save channel');
    } finally {
      setSavingChannel(false);
    }
  };

  const handleDeleteChannel = async (ch, e) => {
    e?.stopPropagation?.();
    if (!(await confirm({ title: `Delete channel "${ch.name}"?`, description: 'All its messages are removed. This cannot be undone.', confirmText: 'Delete channel', destructive: true }))) return;
    try {
      await chatApi.deleteChannel(ch.id);
      toast.success('Channel deleted');
      if (activeChannel === ch.id) setActiveChannel('general');
      loadChannels();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const activeChannelData = channels.find(c => c.id === activeChannel);
  const displayMessages = searchOpen && searchResults.length > 0 ? searchResults : messages;

  // Render message text with @mentions highlighted
  const renderMessageText = (text, isMe) => {
    if (!text) return null;
    const parts = text.split(/(@[A-Za-z][A-Za-z0-9_-]{1,30})/g);
    const myKey = (user?.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
    return (
      <p className="whitespace-pre-wrap break-words">
        {parts.map((p, i) => {
          if (p.startsWith('@')) {
            const handleKey = p.slice(1).toLowerCase();
            const isMyMention = handleKey === myKey;
            const bgClass = isMyMention
              ? 'bg-amber-500/30 text-amber-200 font-semibold'
              : isMe ? 'bg-teal-700/60 text-white' : 'bg-teal-600/20 text-teal-300';
            return <span key={i} className={`${bgClass} rounded px-1`}>{p}</span>;
          }
          return <span key={i}>{p}</span>;
        })}
      </p>
    );
  };

  // =================== MESSAGE RENDER ===================
  const renderAttachment = (msg) => {
    const att = msg.attachment;
    if (!att) return null;
    const token = localStorage.getItem('token');
    const fileUrl = `${API}/api/chat/file/${msg.id}`;

    if (att.is_image) {
      return (
        <div className="mt-1.5 rounded-lg overflow-hidden max-w-[280px]" data-testid="chat-image-attachment">
          <img
            src={fileUrl + `?token=${token}`}
            alt={att.filename}
            className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(fileUrl, '_blank')}
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
          />
          <a href={fileUrl} target="_blank" rel="noreferrer" className="hidden items-center gap-2 p-2 text-xs text-teal-400 hover:text-teal-300">
            <Image className="h-3.5 w-3.5" /> {att.filename}
          </a>
        </div>
      );
    }

    return (
      <a href={fileUrl} target="_blank" rel="noreferrer"
        className="mt-1.5 flex items-center gap-2 p-2 rounded-lg bg-slate-700/50 border border-slate-600 hover:border-teal-500/50 transition-colors max-w-[280px]"
        data-testid="chat-file-attachment"
      >
        <FileText className="h-5 w-5 text-teal-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-white truncate">{att.filename}</p>
          <p className="text-[10px] text-slate-400">{formatFileSize(att.size)}</p>
        </div>
        <Download className="h-3.5 w-3.5 text-slate-400" />
      </a>
    );
  };

  // =================== RENDER ===================
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col" data-testid="team-chat-page">
      <MobileNav navItems={chatNavItems} user={user} onLogout={handleLogout} />

      <div className="flex flex-1 overflow-hidden">
        {/* Channel Sidebar */}
        <aside className="hidden lg:flex w-64 flex-col bg-slate-950 border-r border-slate-800">
          <div className="p-4 border-b border-slate-800">
            <LogoHome className="h-12 w-auto mb-4" />
            <Link to={backRoute} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-3">
              <ArrowLeft className="h-4 w-4" /> Back to Dashboard
            </Link>
            <h2 className="text-white font-semibold">Team Chat</h2>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {channels.map((ch) => (
              <button
                key={ch.id}
                onClick={() => setActiveChannel(ch.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-colors group ${
                  activeChannel === ch.id
                    ? 'bg-teal-600/20 text-teal-400 border border-teal-600/30'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`channel-${ch.id}`}
              >
                {iconFor(ch)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate flex items-center gap-1.5">
                    {ch.name}
                    {ch.is_default === false && (
                      <span className="text-[9px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded">CUSTOM</span>
                    )}
                  </p>
                  {ch.last_message && (
                    <p className="text-xs text-slate-500 truncate">
                      {ch.last_message.sender_name}: {ch.last_message.attachment ? '(file)' : ch.last_message.text}
                    </p>
                  )}
                </div>
                {canManageChannel(ch) && (
                  <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                    <span
                      role="button"
                      tabIndex={0}
                      className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-teal-400 cursor-pointer"
                      onClick={(e) => openEditChannel(ch, e)}
                      onKeyDown={(e) => e.key === 'Enter' && openEditChannel(ch, e)}
                      data-testid={`edit-channel-${ch.id}`}
                      aria-label="Edit channel"
                    >
                      <Pencil className="h-3 w-3" />
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400 cursor-pointer"
                      onClick={(e) => handleDeleteChannel(ch, e)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDeleteChannel(ch, e)}
                      data-testid={`delete-channel-${ch.id}`}
                      aria-label="Delete channel"
                    >
                      <Trash2 className="h-3 w-3" />
                    </span>
                  </div>
                )}
                {ch.unread_count > 0 ? (
                  <Badge className="bg-teal-600 text-white text-xs min-w-[20px] flex items-center justify-center">{ch.unread_count}</Badge>
                ) : ch.message_count > 0 ? (
                  <Badge variant="secondary" className="bg-slate-700 text-slate-400 text-xs">{ch.message_count}</Badge>
                ) : null}
              </button>
            ))}
            <button
              onClick={openCreateChannel}
              className="w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-colors text-slate-500 hover:bg-slate-800 hover:text-teal-400 border border-dashed border-slate-800 hover:border-teal-600/40 mt-2"
              data-testid="create-channel-btn"
            >
              <Plus className="h-4 w-4" />
              <span className="text-sm font-medium">New Channel</span>
            </button>
          </div>
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-9 w-9 rounded-full bg-teal-600 flex items-center justify-center text-white text-sm font-medium">{user?.name?.charAt(0)}</div>
              <div><p className="text-white text-sm font-medium">{user?.name}</p><p className="text-slate-500 text-xs capitalize">{user?.role}</p></div>
            </div>
            <Button variant="outline" size="sm" className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </aside>

        {/* Mobile Channel Tabs */}
        <div className="lg:hidden flex-shrink-0">
          <div className="flex overflow-x-auto bg-slate-950 border-b border-slate-800 px-2 py-2 gap-1">
            {channels.map((ch) => (
              <button key={ch.id} onClick={() => setActiveChannel(ch.id)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors relative ${
                  activeChannel === ch.id ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400'
                }`}
              >
                {ch.name}
                {ch.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center">{ch.unread_count}</span>
                )}
              </button>
            ))}
            <button
              onClick={openCreateChannel}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium bg-slate-800 text-teal-400 border border-dashed border-slate-700 flex items-center gap-1"
              data-testid="mobile-create-channel-btn"
            >
              <Plus className="h-3 w-3" /> New
            </button>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat Header */}
          <div className="flex-shrink-0 px-4 lg:px-6 py-3 bg-slate-800/50 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {activeChannelData ? iconFor(activeChannelData) : <Hash className="h-4 w-4 text-teal-400" />}
                <h3 className="text-white font-semibold">{activeChannelData?.name || activeChannel}</h3>
                <span className="text-slate-500 text-sm hidden sm:block">- {activeChannelData?.description || ''}</span>
                {activeChannelData && canManageChannel(activeChannelData) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-slate-500 hover:text-teal-400"
                    onClick={(e) => openEditChannel(activeChannelData, e)}
                    data-testid="header-edit-channel-btn"
                    title="Edit channel"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => { setSearchOpen(!searchOpen); setSearchResults([]); setSearchQuery(''); }}
                  className={searchOpen ? 'text-teal-400' : 'text-slate-500'} data-testid="toggle-search-btn">
                  <Search className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={toggleNotifications}
                  className={notificationsEnabled ? 'text-teal-400' : 'text-slate-500'} data-testid="toggle-notifications-btn">
                  {notificationsEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            {searchOpen && (
              <div className="mt-2 flex gap-2" data-testid="chat-search-bar">
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search messages..."
                  className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500 h-9 text-sm"
                  data-testid="search-input"
                  autoFocus
                />
                <Button size="sm" onClick={handleSearch} disabled={searching} className="bg-teal-600 hover:bg-teal-700 h-9" data-testid="search-btn">
                  {searching ? '...' : 'Search'}
                </Button>
                {searchResults.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-slate-400 h-9" onClick={() => { setSearchResults([]); setSearchQuery(''); }}>
                    <X className="h-3.5 w-3.5 mr-1" /> Clear
                  </Button>
                )}
              </div>
            )}
            {searchOpen && searchResults.length > 0 && (
              <p className="text-xs text-slate-500 mt-1">{searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found</p>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 lg:px-6 py-4 space-y-3" data-testid="messages-container">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : displayMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                {searchOpen ? (
                  <>
                    <Search className="h-12 w-12 text-slate-600 mb-4" />
                    <p className="text-slate-400">{searchQuery ? 'No messages found' : 'Search for messages'}</p>
                  </>
                ) : (
                  <>
                    <MessageSquare className="h-12 w-12 text-slate-600 mb-4" />
                    <p className="text-slate-400">No messages yet</p>
                    <p className="text-slate-500 text-sm">Start the conversation!</p>
                  </>
                )}
              </div>
            ) : (
              displayMessages.map((msg) => {
                const isMe = msg.sender_id === user?.id;
                const readCount = (msg.read_by || []).length;
                const mentionsMe = (msg.mentioned_user_ids || []).includes(user?.id);
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid="chat-message">
                    <div className="max-w-[75%]">
                      <div className={`flex items-center gap-2 mb-0.5 ${isMe ? 'justify-end' : ''}`}>
                        {!isMe && <span className="text-xs font-medium text-teal-400">{msg.sender_name}</span>}
                        <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500 px-1 py-0">{msg.sender_role}</Badge>
                        {mentionsMe && (
                          <Badge className="text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 px-1 py-0" data-testid="mention-badge">
                            @you
                          </Badge>
                        )}
                        <span className="text-[10px] text-slate-600">
                          {new Date(msg.created_at).toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {searchOpen && msg.channel !== activeChannel && (
                          <Badge variant="outline" className="text-[10px] border-teal-600/50 text-teal-400 px-1 py-0">#{msg.channel}</Badge>
                        )}
                      </div>
                      <div className={`px-3 py-2 rounded-xl text-sm ${
                        isMe
                          ? 'bg-teal-600 text-white rounded-br-sm'
                          : mentionsMe
                            ? 'bg-amber-500/10 text-slate-200 border border-amber-500/30 rounded-bl-sm'
                            : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-bl-sm'
                      }`}>
                        {renderMessageText(msg.text, isMe)}
                        {renderAttachment(msg)}
                      </div>
                      {isMe && (
                        <div className="flex items-center justify-end gap-1 mt-0.5" data-testid="read-receipt">
                          <CheckCheck className={`h-3 w-3 ${readCount > 1 ? 'text-teal-400' : 'text-slate-500'}`} />
                          {readCount > 1 && <span className="text-[10px] text-slate-500">Read by {readCount - 1}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            {typingUsers.length > 0 && !searchOpen && (
              <div className="flex items-center gap-2 px-2" data-testid="typing-indicator">
                <div className="flex gap-1">
                  <span className="h-1.5 w-1.5 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-1.5 w-1.5 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-1.5 w-1.5 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-slate-400">{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Pending File Preview */}
          {pendingFile && (
            <div className="flex-shrink-0 px-4 lg:px-6 py-2 bg-slate-800 border-t border-slate-700" data-testid="pending-file-preview">
              <div className="flex items-center gap-3 bg-slate-900 rounded-lg p-2">
                {pendingFile.type?.startsWith('image/') ? (
                  <img src={URL.createObjectURL(pendingFile)} alt="" className="h-10 w-10 rounded object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded bg-slate-700 flex items-center justify-center"><FileText className="h-5 w-5 text-teal-400" /></div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{pendingFile.name}</p>
                  <p className="text-xs text-slate-500">{formatFileSize(pendingFile.size)}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={cancelFile} className="text-slate-400 hover:text-red-400">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Message Input */}
          <div className="flex-shrink-0 px-4 lg:px-6 py-3 bg-slate-800/50 border-t border-slate-700 relative">
            {/* @mention autocomplete dropdown */}
            {mentionOpen && mentionResults.length > 0 && (
              <div className="absolute bottom-full left-4 right-4 lg:right-40 mb-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl overflow-hidden z-50" data-testid="mention-dropdown">
                <div className="px-3 py-1.5 text-[10px] text-slate-500 uppercase tracking-wide border-b border-slate-800 bg-slate-950/50">
                  Mention a team member · ↑↓ navigate · ↵ insert
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {mentionResults.slice(0, 8).map((u, i) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => insertMention(u)}
                      onMouseEnter={() => setMentionIndex(i)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                        i === mentionIndex ? 'bg-teal-600/20' : 'hover:bg-slate-800'
                      }`}
                      data-testid={`mention-option-${u.handle}`}
                    >
                      <div className="h-7 w-7 rounded-full bg-teal-600 text-white text-xs font-medium flex items-center justify-center shrink-0">
                        {u.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">{u.name}</p>
                        <p className="text-[10px] text-slate-500 capitalize">@{u.handle} · {u.role}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <form onSubmit={handleSend} className="flex gap-2 pr-0 lg:pr-36">
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" data-testid="file-input"
                accept="image/*,.pdf,.doc,.docx,.txt,.csv" />
              <Button type="button" variant="ghost" size="sm" className="text-slate-400 hover:text-teal-400 px-2 flex-shrink-0"
                onClick={() => fileInputRef.current?.click()} data-testid="attach-file-btn">
                <Paperclip className="h-5 w-5" />
              </Button>
              <Input
                ref={chatInputRef}
                value={newMessage}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                onBlur={() => setTimeout(() => setMentionOpen(false), 200)}
                placeholder={pendingFile ? `Add a caption (optional)...` : `Message #${activeChannelData?.name || activeChannel}... (type @ to mention)`}
                className="flex-1 bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="chat-input"
                disabled={sending}
              />
              <Button type="submit" disabled={(!newMessage.trim() && !pendingFile) || sending}
                className="bg-teal-600 hover:bg-teal-700" data-testid="send-message-btn">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Create / Edit Channel Dialog */}
      <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
        <DialogContent className="max-w-md bg-slate-900 text-white border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingChannel ? <><Pencil className="h-4 w-4 text-teal-400" /> Edit Channel</> : <><Plus className="h-4 w-4 text-teal-400" /> New Channel</>}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-300">Channel Name</Label>
              <Input
                value={chanName}
                onChange={(e) => setChanName(e.target.value)}
                placeholder="e.g. Night Shift Crew"
                maxLength={40}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="channel-name-input"
              />
              <p className="text-[10px] text-slate-500 mt-1">{chanName.length}/40</p>
            </div>
            <div>
              <Label className="text-sm text-slate-300">Description (optional)</Label>
              <Textarea
                value={chanDesc}
                onChange={(e) => setChanDesc(e.target.value)}
                placeholder="What's this channel about?"
                rows={2}
                maxLength={160}
                className="bg-slate-800 border-slate-700 text-white mt-1"
                data-testid="channel-desc-input"
              />
            </div>
            <div>
              <Label className="text-sm text-slate-300">Icon</Label>
              <Select value={chanIcon} onValueChange={setChanIcon}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1" data-testid="channel-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hash"># Hash</SelectItem>
                  <SelectItem value="heart">♥ Heart (support/care)</SelectItem>
                  <SelectItem value="users">◉ Users (team)</SelectItem>
                  <SelectItem value="star">★ Star (highlights)</SelectItem>
                  <SelectItem value="lightbulb">💡 Lightbulb (ideas)</SelectItem>
                  <SelectItem value="calendar">📅 Calendar (scheduling)</SelectItem>
                  <SelectItem value="alert">⚠ Alert (important)</SelectItem>
                  <SelectItem value="refresh">↻ Refresh (ongoing)</SelectItem>
                  <SelectItem value="utensils">🍴 Utensils (meals)</SelectItem>
                  <SelectItem value="globe">🌐 Globe (cultural)</SelectItem>
                  <SelectItem value="chef-hat">👨‍🍳 Chef Hat (recipes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setChannelDialogOpen(false)} disabled={savingChannel} className="border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button
                onClick={handleSaveChannel}
                disabled={savingChannel || chanName.trim().length < 2}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="save-channel-btn"
              >
                {savingChannel ? 'Saving...' : editingChannel ? 'Save Changes' : 'Create Channel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
